import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useEnrichmentVersion } from '../EnrichmentVersionContext';

type AutofillTextFieldProps = Omit<TextFieldProps, 'value'> & {
  /**
   * Stable identifier for this field (e.g. `"location-description"`). Combined
   * with the current enrichment version to build the remount `key` — callers
   * never construct the key string themselves, so there's no risk of two
   * fields accidentally sharing a key or forgetting the version suffix.
   */
  autofillId: string;
  /**
   * Required controlled value, including during enrichment updates.
   * `null` is allowed for backend values; callers should coalesce numeric
   * values to `''` when needed (for example, `data.minNumberOfSpaces ?? ''`).
   */
  value: string | number | null;
};

/**
 * Drop-in replacement for MUI's `TextField` for any field that location
 * enrichment (autofill) can populate programmatically. Remounts the field
 * whenever `EnrichmentVersionContext` changes, so the label-shrink state is
 * re-derived from the new value instead of relying on a native input event
 * that autofill never fires. See `EnrichmentVersionContext.tsx` for details.
 *
 * Usage: swap `<TextField ... />` for `<AutofillTextField autofillId="location-information-name" ... />`.
 * Namespace `autofillId` by owning step (e.g. `location-settings-description`) to avoid
 * accidental collisions between fields in different steps. Always pass `value`
 * explicitly; this component is only ever meant to be
 * used in controlled mode.
 */
const AutofillTextField = (props: AutofillTextFieldProps) => {
  const { autofillId, value, ...textFieldProps } = props;
  const version = useEnrichmentVersion();
  return <TextField key={`${autofillId}-${version}`} {...textFieldProps} value={value ?? ''} />;
};

export default AutofillTextField;
